import { isEqual } from "lodash";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

type CallbackOrVal<T> = T | ((prevState: T) => T);

let queryUpdateQueue: Record<string, string | null> = {};
let updateTimeout: NodeJS.Timeout | null = null;

const appendQueryParams = (newParams: Record<string, string | null>) => {
  Object.entries(newParams).forEach(([key, value]) => {
    // null means "delete this key" — keep it in the queue as a sentinel
    queryUpdateQueue[key] = value;
  });

  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }

  updateTimeout = setTimeout(() => {
    const currentParams = new URLSearchParams(window.location.search);
    let changed = false;

    Object.entries(queryUpdateQueue).forEach(([key, value]) => {
      if (value === null) {
        if (currentParams.has(key)) {
          currentParams.delete(key);
          changed = true;
        }
      } else if (currentParams.get(key) !== value) {
        currentParams.set(key, value);
        changed = true;
      }
    });

    if (changed) {
      const query = currentParams.toString();
      const newUrl = query
        ? `${window.location.pathname}?${query}`
        : window.location.pathname;
      window.history.replaceState(null, "", newUrl);
    }

    queryUpdateQueue = {};
    updateTimeout = null;
  }, 10);
};

const defaultSerialize = <T,>(value: T | null): string | null => {
  if (value === null) return null;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return value.toString();
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const defaultDeserialize = <T,>(value: string | null): T | null => {
  if (value === null) return null;

  if (value === "true" || value === "false") {
    return (value === "true") as T;
  }

  // Prefer JSON for objects/arrays before numeric coercion ("[]", "[1]", "{}")
  if (value.startsWith("{") || value.startsWith("[")) {
    try {
      return JSON.parse(value) as T;
    } catch {
      return value as T;
    }
  }

  // Avoid Number("") === 0
  if (value !== "" && !isNaN(Number(value))) {
    return Number(value) as T;
  }

  return value as T;
};

function useAppendSearchState<T>(
  key: string,
  initialValue: T | null,
  options?: {
    serialize?: (value: T | null) => string | null;
    deserialize?: (value: string | null) => T | null;
  },
): [T | null, (v: CallbackOrVal<T | null>) => void] {
  const location = useLocation();
  // Depend on the function identities, not the options object (new {} every render)
  const serializeFn = options?.serialize;
  const deserializeFn = options?.deserialize;

  const serialize = useMemo(
    () => serializeFn || defaultSerialize<T>,
    [serializeFn],
  );

  const deserialize = useMemo(
    () => deserializeFn || defaultDeserialize<T>,
    [deserializeFn],
  );

  const [value, setValueState] = useState<T | null>(() => {
    const params = new URLSearchParams(location.search);
    const urlValue = params.get(key);
    return urlValue !== null ? deserialize(urlValue) : initialValue;
  });

  const setValue = useCallback((v: CallbackOrVal<T | null>) => {
    setValueState((prev) => {
      const next = typeof v === "function" ? (v as (p: T | null) => T | null)(prev) : v;
      // Prevent update-depth loops from setX([]) when state is already []
      if (isEqual(prev, next)) {
        return prev;
      }
      return next;
    });
  }, []);

  const lastWrittenRef = useRef<string | null>(null);

  useEffect(() => {
    const serializedValue = serialize(value);

    if (serializedValue === null) {
      if (lastWrittenRef.current === null) {
        return;
      }
      lastWrittenRef.current = null;
      appendQueryParams({ [key]: null });
      return;
    }

    if (lastWrittenRef.current === serializedValue) {
      return;
    }

    // Also skip if the live URL already has this value (e.g. hydrated from refresh)
    const current = new URLSearchParams(window.location.search).get(key);
    if (current === serializedValue) {
      lastWrittenRef.current = serializedValue;
      return;
    }

    lastWrittenRef.current = serializedValue;
    appendQueryParams({
      [key]: serializedValue,
    });
  }, [value, key, serialize]);

  return [value, setValue];
}

export default useAppendSearchState;
